/**
 * branding.js — print-px geometry of one coBranding asset (logo / ship inlay), mirroring the
 * production print generator EXACTLY so the preview shows what will be printed:
 *
 *   AppFactory.generateImageDOMElements sizes the on-screen <img> at
 *     onScreenW = naturalW/100 * sizePercentage,  onScreenH = naturalH/100 * sizePercentage
 *   imageGenerator.#addBranding draws it on the print canvas at
 *     width = onScreenW * 9.4,  height = onScreenH * 9.4
 *   centred on (xPos% of canvas width, yPos% of canvas height measured from the bottom).
 *
 * The size is ABSOLUTE print px — identical on every print size — not a fraction of the card.
 * (9.4 ≈ 7087/755: print canvas px/cm over demo card px/cm. It equals a fraction of a fixed
 * 755×566 card only on a 45×60 canvas, which is also what 30×40 renders on after its 1.5
 * downscale inflation. The old "fraction of the card" maths drew A4 assets at ~0.70× print.)
 */
export const PRINT_LOGO_SCALE = 9.4;

function pick(logo, isPortrait, key) {
  return parseFloat(isPortrait ? logo[`portrait${key}`] : logo[`landscape${key}`]);
}

/** {wPrint, hPrint} in print px from the asset's natural size and the config's sizePercentage. */
export function coBrandingPrintSize(logo, natural, isPortrait) {
  const sizePct = pick(logo, isPortrait, "SizePercentage");
  return {
    wPrint: (natural.w / 100) * sizePct * PRINT_LOGO_SCALE,
    hPrint: (natural.h / 100) * sizePct * PRINT_LOGO_SCALE,
  };
}

/**
 * Clamp a bar so it never crosses into a logo on its growth side. anchorX + align say where the
 * bar starts and which way it grows; desiredW is the unclamped width; pad is the min gap kept
 * from a logo. Only logos whose visible box vertically overlaps `band` {top, bottom} count — a
 * logo above/below the bar's row can't collide with it. Floors at a quarter of desiredW so a bar
 * never collapses to nothing.
 */
export function clampWidthToLogos(anchorX, align, desiredW, extents, pad, band) {
  let w = desiredW;
  for (const e of extents) {
    if (e.bottom <= band.top || e.top >= band.bottom) continue;
    if (align === "right") {
      // grows LEFT from anchorX: a logo whose visible right edge is left of the anchor caps reach.
      if (e.right <= anchorX) w = Math.min(w, anchorX - (e.right + pad));
    } else {
      // grows RIGHT from anchorX: a logo whose visible left edge is right of the anchor caps reach.
      if (e.left >= anchorX) w = Math.min(w, (e.left - pad) - anchorX);
    }
  }
  return Math.max(w, desiredW * 0.25);
}

/** Full print-px box {x, y, w, h} (SVG top-left) on a printCanvas {width, height}. */
export function coBrandingPrintBox(logo, natural, isPortrait, printCanvas) {
  const { wPrint, hPrint } = coBrandingPrintSize(logo, natural, isPortrait);
  const W = printCanvas.width;
  const H = printCanvas.height;
  const xPos = pick(logo, isPortrait, "XPos");
  const yPos = pick(logo, isPortrait, "YPos");
  const x = (xPos / 100) * W - wPrint / 2;                 // centred on xPos%
  const bottomEdgeFromBottom = (yPos / 100) * H - hPrint / 2; // CSS bottom of the box
  const y = H - bottomEdgeFromBottom - hPrint;              // SVG top
  return { x, y, w: wPrint, h: hPrint };
}
